/* @flow */

import {observable, action, computed} from 'mobx';
import {Validator, ValidationError} from './form';
import {ValueFieldData} from './value-field-data';

class UploadedValidator<T> extends Validator<UploadableFileFieldData<T>> {
    @computed get error() {
        const {fieldData} = this;

        if (!fieldData.uploaded) {
            return new ValidationError();
        }
        return null;
    }
}

export class UploadableFileFieldData<T> extends ValueFieldData<T> {
    handleUpload: (imageFieldData: UploadableFileFieldData<T>) => Promise<T>;
    handleCancel: ?(imageFieldData: UploadableFileFieldData<T>) => Promise<any>;

    @observable thumbnail: ?string;
    @observable progress: ?number;
    @observable uploading = false;
    @observable uploaded = false;
    @observable error: ?Error;

    file: ?File;

    constructor(options) {
        super({
            ...options,
            validators: [
                new UploadedValidator(),
                ...(options.validators || []),
            ],
        });

        const {file, value, thumbnail, handleUpload, handleCancel, startUpload = true} = options;

        if (value) {
            this.uploaded = true;
            this.key = (value instanceof Object && value.id) || Math.random();
        } else if (file) {
            this.key = file.name;
        }

        this.thumbnail = thumbnail;

        if (handleUpload) {
            this.handleUpload = handleUpload;
        }

        if (handleCancel) {
            this.handleCancel = handleCancel;
        }

        this.file = file;



        if (file && startUpload) {
            this.upload();
        }
    }

    setFile(file: File) {
        this.file = file;
    }

    @action uploadDidSucceed = (value: T) => {
        this.uploaded = true;
        this.uploading = false;
        this.setValue({value});
    }

    @action uploadDidFail = (err: Error) => {
        console.error(err);

        this.uploading = false;
        this.error = err;
    }

    @action upload = () => {
        this.uploaded = false;
        this.uploading = true;
        this.error = null;

        this.progress = null;
        const promise = this.handleUpload(this);
        this.promise = promise;

        promise
        .then(this.uploadDidSucceed)
        .catch(this.uploadDidFail);

        return promise;
    }

    cancel() {
        if (this.promise && this.promise.isPending()) {
            this.promise.cancel();
        }

        if (this.dirty && this.file && this.handleCancel) {
            return this.handleCancel(this);
        }

        return super.cancel();
    }

    @action setProgress({progress}: {progress: number}) {
        this.progress = progress;
    }
}
